import CorporateAuthorRepository from "./../api/index"
import { isAuthorLanguage } from "../types"
import "../types"

const FORM_FIELDS = [
    "author_name",
    "author_slug",
    "author_desc",
    "author_facebook_url",
    "author_x_url",
    "author_in_url",
    "author_banner",
    "author_banner_mobile",
    "author_avatar",
    "author_status",
]

/**
 * Entity/use-case orchestration for Corporate Author. Depends only on the
 * injected repository contract — never touches HTTP, DTO parsing, or
 * FormData directly. See app/types/index.js for the CorporateAuthorRepository
 * contract that a caller must provide.
 */
export class CorporateAuthorUseCase {
    /** @param {CorporateAuthorRepository} repository */
    constructor(repository) {
        /** @type {CorporateAuthorRepository} */
        this.repository = repository
    }

    /**
     * @param {CorporateAuthorListQuery} query
     * @return {Promise<RepositoryResult<CorporateAuthor[]>>}
     */
    async getAll(query) {
        if (query && query.language !== undefined && !isAuthorLanguage(query.language)) {
            return { data: null, err: "invalid language" }
        }
        const { data, err } = await this.repository.getAll(query)
        if (err) {
            return { data: null, err }
        }
        return { data: data || [], err: null }
    }

    /** @param {number|string} id */
    async getOne(id) {
        return this.repository.getOne(id)
    }

    /** @param {string} name @param {AuthorLanguage=} language */
    async checkAvailable(name, language) {
        if (language !== undefined && !isAuthorLanguage(language)) {
            return { data: null, err: "invalid language" }
        }
        return this.repository.checkAvailable(name, language)
    }

    /** @param {CorporateAuthorForm} form */
    async createOne(form) {
        if (!isAuthorLanguage(form.language)) {
            return { data: null, message: "", err: "invalid language" }
        }
        return this.repository.createOne(form)
    }

    /**
     * Clone a persisted entity into a complete, independent form object.
     * Never returns a reference to the repository's entity.
     *
     * @param {CorporateAuthor} author
     * @param {AuthorLanguage} legacyFallback
     * @return {{form: CorporateAuthorForm, meta: CorporateAuthorHydrationMeta}}
     */
    hydrateForm(author, legacyFallback) {
        const hadPersistedLanguage = isAuthorLanguage(author.language)
        const language = hadPersistedLanguage ? author.language : legacyFallback

        const form = FORM_FIELDS.reduce((acc, field) => {
            acc[field] = author[field]
            return acc
        }, /** @type {CorporateAuthorForm} */ ({}))
        form.language = language

        const meta = {
            hadPersistedLanguage,
            languageDirty: false,
            persistedLanguage: hadPersistedLanguage ? author.language : null,
        }

        return { form, meta }
    }

    /**
     * Build the partial update payload. Omits the legacy fallback language
     * silently — it is only written once the user makes an explicit change.
     *
     * @param {CorporateAuthorForm} form
     * @param {CorporateAuthorHydrationMeta} meta
     * @return {Partial<CorporateAuthorForm>}
     */
    buildUpdateCommand(form, meta) {
        const command = FORM_FIELDS.reduce((acc, field) => {
            acc[field] = form[field]
            return acc
        }, {})

        if (meta.hadPersistedLanguage || meta.languageDirty) {
            command.language = form.language
        }

        return command
    }

    /**
     * @param {number|string} id
     * @param {Partial<CorporateAuthorForm>} command
     */
    async updateOne(id, command) {
        return this.repository.updateOne(id, command)
    }
}

export default new CorporateAuthorUseCase(new CorporateAuthorRepository())
